import React from 'react';
import { Box, Container, Divider, Paper, Stack, Typography } from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'left',
        }}
      >
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </Stack>

        <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }} paragraph>
          This Privacy Policy explains how Lexory ("we", "us", or "our") collects, uses, and shares information when you use
          the Lexory website and services (the "Service").
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Information we collect
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }} paragraph>
              We collect information you provide directly to us, such as when you create an account, sign in, contact support, or
              subscribe to Premium.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }} paragraph>
              This may include:
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3, '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Account information (for example: email address).
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Usage information related to your interaction with the Service.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Payment-related information handled by our payment provider (Stripe).
                </Typography>
              </li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Content you process
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              The Service may process text you provide for translation or reading features. Depending on how you use the Service,
              this text can include personal or sensitive content. You are responsible for the content you submit.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              How we use information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }} paragraph>
              We use information to:
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3, '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Provide, maintain, and improve the Service.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Authenticate you and manage your account.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Measure usage for plan limits and billing purposes.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Communicate with you about the Service (for example: password resets).
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Prevent fraud, abuse, and security incidents.
                </Typography>
              </li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              How we share information
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }} paragraph>
              We may share information with:
            </Typography>
            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3, '& li': { mb: 1 } }}>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Service providers that help operate the Service (for example: hosting, analytics, authentication, and payment
                  processing).
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Payment processor (Stripe) to handle purchases, subscriptions, and related billing operations.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
                  Legal or regulatory authorities if required by law or to protect rights, safety, and security.
                </Typography>
              </li>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Data retention
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              We retain information for as long as necessary to provide the Service and for legitimate business purposes, such as
              complying with legal obligations, resolving disputes, and enforcing agreements.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Security
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              We use reasonable administrative, technical, and physical safeguards designed to protect information. No method of
              transmission or storage is 100% secure.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Your choices
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              You can access and update certain account information through the Service. You may also request account deletion or
              contact us with privacy questions.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              International transfers
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              Your information may be processed in countries other than where you live. These countries may have data protection
              laws that differ from your jurisdiction.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Changes to this policy
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              We may update this Privacy Policy from time to time. We will post the updated version on this page and update the
              "Last updated" date.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Contact
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, fontSize: '1.05rem' }}>
              If you have questions about this Privacy Policy, contact us at lexory@proton.me.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
